import React from 'react';
import { motion } from 'framer-motion';
import { Clock, Lock, PhoneOff, ShieldAlert } from 'lucide-react';

/**
 * PainPoints - light theme, Cleo-style problem framing.
 *
 * Agitates the pain of legacy EDI BEFORE the solutions grid answers it.
 * Sits between the proof stats and the solutions. Four pain cards, each
 * with a sky-tint icon circle, a bold pain title, and a tight description,
 * then a one-line bridge into iConnect.
 */
const PAINS = [
  {
    icon: Clock,
    title: 'Onboarding drags on for months',
    description:
      'New trading partners take forever to connect - and production waits while the maps get built.',
  },
  {
    icon: Lock,
    title: 'VAN lock-in & surprise surcharges',
    description:
      'Opaque per-kilocharacter fees and rigid contracts you can’t leave when priorities change.',
  },
  {
    icon: PhoneOff,
    title: 'Support black holes',
    description:
      'Tickets disappear into the void - offshore reps who don’t actually know EDI.',
  },
  {
    icon: ShieldAlert,
    title: 'Compliance pressure',
    description:
      'OEM mandates, MMOG/LE, and ASN penalties leave no room for getting it wrong.',
  },
];

const PainPoints = () => {
  return (
    <section className="relative py-24 lg:py-32 bg-bg-soft">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0.5, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.5 }}
          className="text-center mb-14 max-w-3xl xl:max-w-none mx-auto"
        >
          <p className="eyebrow">The Problem</p>
          {/* xl:whitespace-nowrap keeps this on one line on laptops/desktops at
              full size; it wraps normally below xl so phones never overflow. */}
          <h2 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl text-text-primary mb-4 tracking-[-0.01em] text-balance xl:whitespace-nowrap">
            <span className="text-gradient-sky">EDI</span>{' '}
            should not be this painful
          </h2>
          <p className="text-lg text-text-secondary text-pretty">
            Most companies don't hate EDI. They hate the delays, poor support, hidden costs.
          </p>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
          {PAINS.map((pain, idx) => {
            const Icon = pain.icon;
            return (
              <motion.div
                key={idx}
                initial={{ opacity: 0.5, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-50px' }}
                transition={{ duration: 0.5, delay: idx * 0.08 }}
                whileHover={{ y: -4 }}
                className="card-elevated p-6 lg:p-7"
              >
                <div className="w-11 h-11 rounded-full bg-accent-soft flex items-center justify-center mb-4">
                  <Icon size={20} className="text-accent-deep" />
                </div>
                <h3 className="font-display font-bold text-lg text-text-primary mb-2">
                  {pain.title}
                </h3>
                <p className="text-text-secondary text-sm leading-relaxed">
                  {pain.description}
                </p>
              </motion.div>
            );
          })}
        </div>

        <motion.p
          initial={{ opacity: 0.5, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-50px' }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="text-center font-display font-bold text-3xl sm:text-4xl lg:text-5xl text-text-primary tracking-[-0.01em] text-balance mt-14"
        >
          There's a better way - that's{' '}
          <span>
            <span className="text-accent-primary">i</span><span className="text-text-primary">Connect</span>
          </span>
        </motion.p>
      </div>
    </section>
  );
};

export default PainPoints;
